/**
 * 极简 hash 路由 (无第三方依赖).
 *
 * 路由表:
 *   #/login            登录 (未登录时默认页)
 *   #/register         注册
 *   #/home             首页 (需登录)
 *   #/courses          全部资源 (需登录; 免费资源无需会员即可跟读)
 *   #/practice/:id     跟读详情 (免费资源或会员有效)
 *   #/dictionary       中英文查词 (需登录)
 *   #/profile          我的 (需登录)
 *   #/favorites        我的收藏 (需登录)
 *
 * 说明:
 * - 导航项 (NAV_ITEMS) 与可路由页面 (ALL_ROUTES) 是两份清单:
 *   跟读页不在导航里(从资源进入), 登录/注册也不进导航;
 *   激活会员是状态驱动弹层 (非路由, 见 stores/ui.ts);
 *   我的收藏在底部导航与侧边导航里;
 * - 会员专享资源不进路由门禁: 列表对所有登录用户可见, 无权限时由页面给出开通引导,
 *   最终以后端 403 为准。
 */

export type RouteName =
  | 'login'
  | 'register'
  | 'home'
  | 'courses'
  | 'practice'
  | 'profile'
  | 'dictionary'
  | 'favorites'
  | 'membership'
  | 'orders'

export const DEFAULT_ROUTE: RouteName = 'home'

export const ALL_ROUTES: readonly RouteName[] = [
  'login',
  'register',
  'home',
  'courses',
  'practice',
  'profile',
  'dictionary',
  'favorites',
  'membership',
  'orders',
]

/** 需要登录才能进入 */
export const AUTH_ROUTES: readonly RouteName[] = [
  'home',
  'courses',
  'practice',
  'profile',
  'dictionary',
  'favorites',
  'membership',
  'orders',
]

export interface NavItem {
  route: RouteName
  /** i18n 文案 key, 模板里经 `$store.i18n.t()` 渲染 */
  labelKey: string
  icon: string
}

/** 底部导航与侧边导航共用的一份定义 */
export const NAV_ITEMS: readonly NavItem[] = [
  { route: 'home', labelKey: 'nav.home', icon: 'house' },
  { route: 'courses', labelKey: 'nav.courses', icon: 'list-music' },
  { route: 'dictionary', labelKey: 'nav.dictionary', icon: 'book-open' },
  { route: 'favorites', labelKey: 'nav.favorites', icon: 'bookmark' },
  { route: 'profile', labelKey: 'nav.profile', icon: 'user' },
]

export interface ParsedRoute {
  name: RouteName
  params: Record<string, string>
}

export function parseHash(hash: string): ParsedRoute {
  const path = hash.replace(/^#\/?/, '').split('?')[0] ?? ''
  const [head = '', ...rest] = path.split('/')

  const name = (ALL_ROUTES as readonly string[]).includes(head) ? (head as RouteName) : DEFAULT_ROUTE
  const params: Record<string, string> = {}
  const id = rest[0]
  if ((name === 'practice' || name === 'orders') && id) params['id'] = id

  return { name, params }
}

export function buildHash(name: RouteName, params: Record<string, string> = {}): string {
  const id = params['id']
  return (name === 'practice' || name === 'orders') && id ? `#/${name}/${id}` : `#/${name}`
}

export interface RouterStore {
  name: RouteName
  params: Record<string, string>
  items: readonly NavItem[]
  init(): void
  sync(): void
  go(name: RouteName, params?: Record<string, string>): void
  back(): void
  is(name: RouteName): boolean
}

export function routerStore(): RouterStore {
  return {
    name: DEFAULT_ROUTE,
    params: {},
    items: NAV_ITEMS,

    /** 由 main.ts 在 Alpine 启动后调用 */
    init() {
      this.sync()
      window.addEventListener('hashchange', () => this.sync())
    },

    sync() {
      const route = parseHash(window.location.hash)
      this.name = route.name
      this.params = route.params
    },

    go(name, params) {
      const next = buildHash(name, params)
      if (window.location.hash === next) {
        this.sync()
        return
      }
      window.location.hash = next
    },

    /**
     * 返回上一级页面.
     *
     * 每次路由跳转都是改 hash, 会向浏览器历史压栈, 所以直接 history.back()
     * 就能回到上一页; 直接落地深链 (历史里没有上一页) 时回默认页,
     * 避免把用户带出应用.
     */
    back() {
      if (window.history.length > 1) {
        window.history.back()
        return
      }
      this.go(DEFAULT_ROUTE)
    },

    is(name) {
      return this.name === name
    },
  }
}

/** 供 TS 组件与手势调用 (模板里直接用 $store.router.go) */
export function goTo(name: RouteName, params: Record<string, string> = {}): void {
  const router = window.Alpine?.store('router') as RouterStore | undefined
  router?.go(name, params)
}

/** 返回上一级 (滑动返回手势用) */
export function goBack(): void {
  const router = window.Alpine?.store('router') as RouterStore | undefined
  router?.back()
}
